import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { videoSource } from "@/lib/youtube";
import { resolveMedia } from "@/lib/api";

/*
  Lightweight video lightbox for the Video Gallery (The Reel).
  Supports YouTube, Vimeo (iframe embed) and direct MP4 (HTML5 video).
  Closes on ESC / backdrop / X and returns focus to the trigger.
*/
export const GalleryPlayerModal = ({ open, onClose, item, triggerRef }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      triggerRef?.current?.focus?.();
    };
  }, [open, onClose, triggerRef]);

  if (!open || !item) return null;

  const src = videoSource(item.video_url);
  const vertical = item.orientation === "vertical";

  const media = (
    <>
      {(src.kind === "youtube" || src.kind === "vimeo") && (
        <iframe
          className="h-full w-full"
          src={`${src.embed}?autoplay=1&title=0&byline=0&portrait=0&rel=0&modestbranding=1`}
          title={item.title}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
        />
      )}
      {src.kind === "mp4" && (
        <video
          className="h-full w-full bg-black object-contain"
          src={resolveMedia(src.src)}
          poster={resolveMedia(item.poster) || undefined}
          controls
          autoPlay
          playsInline
        />
      )}
      {!src.kind && (
        <div className="flex h-full w-full items-center justify-center text-white/60">
          This video can't be played.
        </div>
      )}
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="gallery-player-modal"
      style={{ animation: "demoModalFade .3s var(--ease)" }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-orientation={vertical ? "vertical" : "horizontal"}
        className={
          vertical
            ? "relative flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-black outline-none"
            : "relative w-full max-w-5xl overflow-hidden rounded-2xl bg-black outline-none"
        }
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
          aria-label="Close video"
          data-testid="gallery-player-close"
        >
          <X size={20} />
        </button>

        {vertical ? (
          <div className="mx-auto aspect-[9/16] max-h-[78vh] w-auto max-w-[92vw] bg-black">
            {media}
          </div>
        ) : (
          <div className="aspect-video w-full">{media}</div>
        )}

        <div className={`bg-[var(--charcoal)] px-6 py-5 text-white ${vertical ? "w-full" : ""}`}>
          <p className="text-lg font-semibold tracking-tight">{item.title}</p>
          {item.caption && <p className="mt-1 text-sm text-white/60">{item.caption}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
};
